import { Box, FormControl, MenuItem, Select, TextField, Tooltip } from "@mui/material"
import { InfoOutlined } from "@mui-symbols-material/w300"
import type { ChangeEvent, FC } from "react"
import type { ControllerRenderProps } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import { TEST_TEMPLATE_VALUE_COMPARISONS } from "../../constants"
import type { AssociatedTestTemplateFormValues } from "../../schemas"
import { getDataTypeLabel, hasSignificantFigures } from "../../utils"

import TestTemplateInfo from "./TestTemplateInfo"

import { FormBooleanRadioGroup } from "@/components/UiKit"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import type { TestTemplate } from "@/graphql/codegen/graphql"
import { DataType, TestTemplateComparison } from "@/graphql/codegen/graphql"

interface AssociatedTestTemplateFormProps {
  testTemplate: TestTemplate
}

const AssociatedTestTemplateForm: FC<AssociatedTestTemplateFormProps> = ({ testTemplate }) => {
  const { control, watch, trigger, setValue } = useFormContext<AssociatedTestTemplateFormValues>()
  const hasValueFields = TEST_TEMPLATE_VALUE_COMPARISONS.includes(testTemplate.comparison)
  const minimumValue = watch("minimumValue")
  const maximumValue = watch("maximumValue")
  const dataType = watch("dataType")
  const significantFigures = ["0", "1", "2", "3", "4", "5"]
  const canSelectSignificantFigures = hasSignificantFigures(testTemplate.comparison, dataType as DataType)

  const handleDataTypeChange = (
    field: ControllerRenderProps<AssociatedTestTemplateFormValues, "dataType">,
    value: DataType
  ) => {
    field.onChange(value)
    setValue("significantFigures", "")

    if (minimumValue) {
      trigger("minimumValue")
    }

    if (maximumValue) {
      trigger("maximumValue")
    }
  }

  const handleRangeMinimimValueChange = (
    field: ControllerRenderProps<AssociatedTestTemplateFormValues, "minimumValue">,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    field.onChange(e)

    if (maximumValue) {
      trigger("maximumValue")
    }
  }

  const handleRangeMaximumValueChange = (
    field: ControllerRenderProps<AssociatedTestTemplateFormValues, "maximumValue">,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    field.onChange(e)

    if (minimumValue) {
      trigger("minimumValue")
    }
  }

  const handleSignificantFiguresChange = (
    field: ControllerRenderProps<AssociatedTestTemplateFormValues, "significantFigures">,
    value: string
  ) => {
    field.onChange(value)

    // Revalidate value fields when significant figures change
    if (minimumValue) {
      trigger("minimumValue")
    }

    if (maximumValue) {
      trigger("maximumValue")
    }
  }

  return (
    <Box className='flex flex-col gap-6'>
      <TestTemplateInfo testTemplate={testTemplate} />
      {hasValueFields && (
        <Box className='flex flex-col gap-6'>
          {testTemplate.comparison === TestTemplateComparison.Range && (
            <Controller
              name='dataType'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl className='flex-1'>
                  <label id='data-type-label' className={FIELD_LABEL_CLASSES}>
                    Data Type<span className='text-red-600'>*</span>
                  </label>
                  <Select
                    {...field}
                    {...SELECT_STYLING_PROPS}
                    data-testid='data-type-select'
                    labelId='data-type-label'
                    error={!!error}
                    onChange={(e) => handleDataTypeChange(field, e.target.value as DataType)}
                    renderValue={(selected) =>
                      selected ? (
                        getDataTypeLabel(selected as DataType)
                      ) : (
                        <span className='text-gray-500'>Select One</span>
                      )
                    }
                  >
                    {Object.values(DataType).map((value) => (
                      <MenuItem key={value} data-test='significant-figures-option' value={value}>
                        {getDataTypeLabel(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  {error?.message && (
                    <p className={FIELD_ERROR_CLASSES} data-testid='significant-figures-error'>
                      {error.message}
                    </p>
                  )}
                </FormControl>
              )}
            />
          )}
          <Box className='flex gap-6'>
            {testTemplate.comparison === TestTemplateComparison.EqualTo && (
              <Controller
                name='equalToValue'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box className='flex-1'>
                    <label htmlFor='equal-to-value-input' className={FIELD_LABEL_CLASSES}>
                      Equal To Value<span className='text-red-600'>*</span>
                    </label>
                    <TextField
                      {...field}
                      {...TEXT_FIELD_STYLING_PROPS}
                      id='equal-to-value-input'
                      data-testid='equal-to-value-input'
                      placeholder='Add Value'
                      error={!!error}
                    />
                    {error?.message && (
                      <p className={FIELD_ERROR_CLASSES} data-testid='equal-to-value-error'>
                        {error.message}
                      </p>
                    )}
                  </Box>
                )}
              />
            )}
            {testTemplate.comparison === TestTemplateComparison.Minimum && (
              <Controller
                name='minimumValue'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box className='flex-1'>
                    <label htmlFor='minimum-value-input' className={FIELD_LABEL_CLASSES}>
                      Minimum Value<span className='text-red-600'>*</span>
                    </label>
                    <TextField
                      {...field}
                      {...TEXT_FIELD_STYLING_PROPS}
                      id='minimum-value-input'
                      data-testid='minimum-value-input'
                      placeholder='Add Minimum Value'
                      error={!!error}
                    />
                    {error?.message && (
                      <p className={FIELD_ERROR_CLASSES} data-testid='minimum-value-error'>
                        {error.message}
                      </p>
                    )}
                  </Box>
                )}
              />
            )}
            {testTemplate.comparison === TestTemplateComparison.Maximum && (
              <Controller
                name='maximumValue'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box className='flex-1'>
                    <label htmlFor='maximum-value-input' className={FIELD_LABEL_CLASSES}>
                      Maximum Value<span className='text-red-600'>*</span>
                    </label>
                    <TextField
                      {...field}
                      {...TEXT_FIELD_STYLING_PROPS}
                      id='maximum-value-input'
                      data-testid='maximum-value-input'
                      placeholder='Add Maximum Value'
                      error={!!error}
                    />
                    {error?.message && (
                      <p className={FIELD_ERROR_CLASSES} data-testid='maximum-value-error'>
                        {error.message}
                      </p>
                    )}
                  </Box>
                )}
              />
            )}
            {testTemplate.comparison === TestTemplateComparison.Range && (
              <>
                <Controller
                  name='minimumValue'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box className='flex-1'>
                      <label htmlFor='range-minimum-value-input' className={FIELD_LABEL_CLASSES}>
                        Range Min. Value<span className='text-red-600'>*</span>
                      </label>
                      <TextField
                        {...field}
                        {...TEXT_FIELD_STYLING_PROPS}
                        id='range-minimum-value-input'
                        data-testid='range-minimum-value-input'
                        placeholder='Add Min. Range'
                        error={!!error}
                        onChange={(e) => handleRangeMinimimValueChange(field, e)}
                      />
                      {error?.message && (
                        <p className={FIELD_ERROR_CLASSES} data-testid='range-minimum-value-error'>
                          {error.message}
                        </p>
                      )}
                    </Box>
                  )}
                />
                <Controller
                  name='maximumValue'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box className='flex-1'>
                      <label htmlFor='range-maximum-value-input' className={FIELD_LABEL_CLASSES}>
                        Range Max. Value<span className='text-red-600'>*</span>
                      </label>
                      <TextField
                        {...field}
                        {...TEXT_FIELD_STYLING_PROPS}
                        id='range-maximum-value-input'
                        data-testid='range-maximum-value-input'
                        placeholder='Add Max. Range'
                        error={!!error}
                        onChange={(e) => handleRangeMaximumValueChange(field, e)}
                      />
                      {error?.message && (
                        <p className={FIELD_ERROR_CLASSES} data-testid='range-maximum-value-error'>
                          {error.message}
                        </p>
                      )}
                    </Box>
                  )}
                />
              </>
            )}
            {canSelectSignificantFigures && (
              <Controller
                name='significantFigures'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl className='flex-1'>
                    <label id='significant-figures-label' className={FIELD_LABEL_CLASSES}>
                      Significant Figures<span className='text-red-600'>*</span>
                    </label>
                    <Select<string>
                      {...field}
                      {...SELECT_STYLING_PROPS}
                      data-testid='significant-figures-select'
                      labelId='significant-figures-label'
                      error={!!error}
                      onChange={(e) => handleSignificantFiguresChange(field, e.target.value)}
                      endAdornment={
                        <Tooltip title='Number of significant figures after decimal'>
                          <InfoOutlined className='absolute right-10 text-gray-600' />
                        </Tooltip>
                      }
                      renderValue={(selected) => selected || <span className='text-gray-500'>Select One</span>}
                    >
                      {significantFigures.map((value) => (
                        <MenuItem key={value} data-test='significant-figures-option' value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                    {error?.message && (
                      <p className={FIELD_ERROR_CLASSES} data-testid='significant-figures-error'>
                        {error.message}
                      </p>
                    )}
                  </FormControl>
                )}
              />
            )}
          </Box>
        </Box>
      )}
      <Box className='flex gap-6'>
        <FormBooleanRadioGroup
          control={control}
          name='printToAkrochem'
          label='Print to Akrochem'
          variant='yes-no'
          required
          className='flex-1'
          data-testid='print-to-akrochem-radio-group'
        />
      </Box>
    </Box>
  )
}

export default AssociatedTestTemplateForm
